/*
 * Phase 4A Commit 4Ab — admin-login.js
 *
 * Submits the login form to /apps/dashboard/admin/login (App Proxy
 * to Railway /proxy/admin/login). On 200, the response body contains
 * a `token` field (JWT) which we stash in sessionStorage; the queue
 * page reads it back and attaches Authorization: Bearer on every
 * fetch. We then redirect to the wholesale queue.
 *
 * Why sessionStorage instead of the cookie the server also sets:
 *   Shopify's App Proxy STRIPS Set-Cookie response headers from
 *   backend responses before they reach the browser. So the cookie
 *   never lands. Body-returned JWT is the only mechanism that
 *   survives the proxy round-trip.
 *
 * On 423 (locked), display the locked_until countdown. On 401 / 403
 * / 400, display the generic error message.
 */
(function () {
  'use strict';

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  var form = $('[data-admin-login-form]');
  if (!form) return;
  var errorEl = $('[data-admin-login-error]');
  var submitBtn = $('[data-admin-login-submit]');

  function showError(msg) {
    if (!errorEl) return;
    errorEl.hidden = false;
    errorEl.textContent = msg;
  }
  function hideError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  // Endpoint reached via Shopify App Proxy:
  //   browser → livecircadia.ca/apps/dashboard/admin/login
  //   → Shopify forwards to Railway /proxy/admin/login
  //   → HMAC middleware verifies origin
  //   → login handler runs, returns { token, admin } in body
  //     (also sets cookie, but App Proxy strips it on the way back)
  // The direct Railway /admin/login mount remains for CLI tooling
  // (see proxy/index.ts post-deploy hotfix comment).
  var ADMIN_LOGIN_ENDPOINT = '/apps/dashboard/admin/login';
  var ADMIN_LOGIN_STATUS_ENDPOINT = '/apps/dashboard/admin/login/status';
  var ENROL_BEGIN_ENDPOINT = '/apps/dashboard/admin/login/enrol/begin';
  var ENROL_CONFIRM_ENDPOINT = '/apps/dashboard/admin/login/enrol/confirm';
  var INVITE_ACCEPT_ENDPOINT = '/apps/dashboard/admin/login/invite/accept';

  // ─── THE PRE-LAUNCH AUTHENTICATOR SWITCH ────────────────────────────────
  //
  // The markup ships with the code field PRESENT and required. This only ever
  // REMOVES it, and only when the server says the switch is on. That ordering
  // is the safe direction: if this fetch fails, is blocked, or never returns,
  // the page keeps asking for a code - it demands more than may be needed,
  // never less. A page that hid the field on a failed request would lock the
  // operator out of a login that still wants a code.
  //
  // When it is on, the notice is LOUD rather than a silently missing field:
  // an operator who cannot tell "TOTP is off" from "the page is broken" is
  // worse off than one who is told plainly.
  var totpField = $('[data-login-totp-field]');
  var bypassNotice = $('[data-login-bypass-notice]');
  var subtitle = $('[data-login-subtitle]');

  function applyTotpBypass(notice) {
    if (totpField) {
      var input = totpField.querySelector('input[name="totp_code"]');
      // Removing `required` matters as much as hiding: a hidden-but-required
      // field blocks submit with a validation error pointing at nothing.
      if (input) {
        input.required = false;
        input.value = '';
      }
      totpField.hidden = true;
      totpField.setAttribute('data-bypassed', 'true');
    }
    if (subtitle) subtitle.textContent = 'Admin auth requires a password.';
    if (bypassNotice) {
      bypassNotice.textContent = notice || 'Authenticator disabled — pre-launch.';
      bypassNotice.hidden = false;
    }
  }

  fetch(ADMIN_LOGIN_STATUS_ENDPOINT, { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      // Only an explicit `totp_required === false` removes the field. A
      // missing key, a null body or an older deploy leaves it in place.
      if (data && data.totp_required === false) applyTotpBypass(data.notice);
    })
    .catch(function () {
      // Deliberately silent and deliberately inert: the form is already in the
      // stricter state, so there is nothing to recover and nothing to warn
      // about that the operator can act on.
    });
  // Post-login lands on the admin hub (was the wholesale queue before the
  // hub existed). The hub is the home you navigate every tool from.
  var HUB_REDIRECT = '/pages/admin';
  var TOKEN_STORAGE_KEY = 'circadia_admin_jwt';
  // The tab the operator came for, left by admin-dashboard.js when it sent them here (an email links straight to a tab). Used once, and only if it is a plain tab id.
  var RETURN_TAB_KEY = 'circadia_admin_return_tab';
  function afterLoginUrl() {
    try {
      var h = window.sessionStorage.getItem(RETURN_TAB_KEY);
      window.sessionStorage.removeItem(RETURN_TAB_KEY);
      if (h && /^#[a-z0-9][a-z0-9-]{0,39}$/i.test(h)) return HUB_REDIRECT + h;
    } catch (_) {}
    return HUB_REDIRECT;
  }

  // ─── Which second factor the form is asking for ─────────────────────────
  //   'totp'   the authenticator code (the default; the page keeps asking for it if anything below fails)
  //   'backup' one single-use backup code instead
  //   'first'  no code: the person has no authenticator yet and is asking to set one up
  var mode = 'totp';
  var backupField = $('[data-login-backup-field]');
  var useBackupBtn = $('[data-login-use-backup]');
  var firstTimeBtn = $('[data-login-first-time]');
  function setMode(next) {
    mode = next;
    var totpInput = totpField && totpField.querySelector('input[name="totp_code"]');
    var backupInput = backupField && backupField.querySelector('input[name="backup_code"]');
    var bypassed = !!totpField && totpField.getAttribute('data-bypassed') === 'true';
    if (totpField) totpField.hidden = bypassed || mode !== 'totp';
    if (totpInput) {
      totpInput.required = !bypassed && mode === 'totp';
      if (mode !== 'totp') totpInput.value = '';
    }
    if (backupField) backupField.hidden = mode !== 'backup';
    if (backupInput) {
      backupInput.required = mode === 'backup';
      if (mode !== 'backup') backupInput.value = '';
    }
    if (useBackupBtn) useBackupBtn.textContent = mode === 'backup' ? 'Use my authenticator code instead' : 'Use a backup code instead';
    if (firstTimeBtn) firstTimeBtn.textContent = mode === 'first' ? 'I already have an authenticator' : 'First time here? Set up your authenticator';
    if (submitBtn) submitBtn.textContent = mode === 'first' ? 'Continue' : 'Sign in';
    hideError();
  }
  if (useBackupBtn) useBackupBtn.addEventListener('click', function () { setMode(mode === 'backup' ? 'totp' : 'backup'); });
  if (firstTimeBtn) firstTimeBtn.addEventListener('click', function () { setMode(mode === 'first' ? 'totp' : 'first'); });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    hideError();
    if (submitBtn) submitBtn.disabled = true;

    var fd = new FormData(form);
    var body = {
      email: fd.get('email'),
      password: fd.get('password'),
      // Omitted entirely when the field is hidden - the server refuses an
      // EMPTY code when the switch is off, so sending '' would be a different
      // request than sending nothing.
      totp_code: totpField && totpField.hidden ? undefined : fd.get('totp_code'),
    };
    // One of three ways to prove the second factor (or to ask to set one up):
    //   backup mode: a single-use code in place of the authenticator code
    //   first-time mode: no code at all, begin_enrolment tells the server why
    if (mode === 'backup') body.backup_code = fd.get('backup_code');
    if (mode === 'first') body.begin_enrolment = true;

    fetch(ADMIN_LOGIN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok) {
          // Stash the body-returned JWT for the queue page to attach
          // as Authorization: Bearer on subsequent fetches. The cookie
          // the server also set is stripped by Shopify's App Proxy,
          // so we cannot rely on it in the browser.
          if (result.data && result.data.token) {
            try {
              window.sessionStorage.setItem(TOKEN_STORAGE_KEY, result.data.token);
            } catch (_) {
              // sessionStorage may be unavailable in incognito w/
              // third-party cookies blocked; surface a recoverable error.
              showError('Could not store session token. Enable site storage and retry.');
              return;
            }
          } else {
            showError('Login succeeded but server returned no token. Contact support.');
            return;
          }
          window.location.assign(afterLoginUrl());
          return;
        }
        if (result.status === 403 && result.data && result.data.code === 'enrolment_required' && result.data.enrol_token) {
          startEnrolment(result.data.enrol_token);
          return;
        }
        if (result.status === 423 && result.data && result.data.locked_until) {
          var until = new Date(result.data.locked_until);
          var mins = Math.max(1, Math.ceil((until.getTime() - Date.now()) / 60000));
          showError('Too many failed attempts. Try again in ~' + mins + ' minute(s).');
          return;
        }
        showError((result.data && result.data.error) || 'Sign-in failed. Try again.');
      })
      .catch(function (err) {
        showError('Network error: ' + (err && err.message ? err.message : 'try again'));
      })
      .then(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  });

  // ─── Self-enrolment (launch gate 2026-10-26) ────────────────────────────
  // The server answered 403 enrolment_required with a ten-minute enrolment token: the password was right and there is no authenticator yet. The token is NOT a session and goes
  // nowhere but the two enrolment routes. It is held in memory only (never storage), so a reload starts over from the password.
  var enrolPanel = $('[data-login-enrol]');
  var enrolKeyEl = $('[data-login-enrol-key]');
  var enrolLink = $('[data-login-enrol-link]');
  var enrolForm = $('[data-login-enrol-form]');
  var enrolError = $('[data-login-enrol-error]');
  var enrolSubmit = $('[data-login-enrol-submit]');
  var codesPanel = $('[data-login-codes]');
  var codesList = $('[data-login-codes-list]');
  var enrolToken = null;

  function enrolFail(msg) {
    if (!enrolError) return;
    enrolError.hidden = false;
    enrolError.textContent = msg;
  }
  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, status: r.status, data: data || {} };
      });
    });
  }
  function groupKey(secret) {
    return String(secret || '').replace(/(.{4})/g, '$1 ').trim();
  }
  function backToSignIn(msg) {
    enrolToken = null;
    if (enrolPanel) enrolPanel.hidden = true;
    form.hidden = false;
    setMode('totp');
    if (msg) showError(msg);
  }
  function startEnrolment(token) {
    enrolToken = token;
    form.hidden = true;
    if (enrolPanel) enrolPanel.hidden = false;
    if (enrolKeyEl) enrolKeyEl.textContent = 'Preparing your key…';
    postJson(ENROL_BEGIN_ENDPOINT, { enrol_token: token })
      .then(function (res) {
        if (!res.ok) {
          backToSignIn((res.data && res.data.error) || 'Could not start set-up. Sign in again.');
          return;
        }
        if (enrolKeyEl) enrolKeyEl.textContent = groupKey(res.data.secret);
        if (enrolLink && res.data.otpauth_url) enrolLink.setAttribute('href', res.data.otpauth_url);
      })
      .catch(function () {
        backToSignIn('Network error while starting set-up. Try again.');
      });
  }

  if (enrolForm) {
    enrolForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!enrolToken) return;
      if (enrolError) {
        enrolError.hidden = true;
        enrolError.textContent = '';
      }
      var code = String(new FormData(enrolForm).get('enrol_code') || '').trim();
      if (enrolSubmit) enrolSubmit.disabled = true;
      postJson(ENROL_CONFIRM_ENDPOINT, { enrol_token: enrolToken, totp_code: code })
        .then(function (res) {
          if (res.ok && res.data && res.data.token && res.data.backup_codes) {
            try {
              window.sessionStorage.setItem(TOKEN_STORAGE_KEY, res.data.token);
            } catch (_) {
              enrolFail('Could not store session token. Enable site storage and retry.');
              return;
            }
            enrolToken = null;
            showBackupCodes(res.data.backup_codes);
            return;
          }
          if (res.status === 401 && res.data.code === 'bad_code') {
            enrolFail(res.data.error);
            return;
          }
          if (res.status === 423) {
            backToSignIn('Too many failed attempts. Try again later.');
            return;
          }
          backToSignIn((res.data && res.data.error) || 'Set-up could not be completed. Sign in again.');
        })
        .catch(function () {
          enrolFail('Network error. Try again.');
        })
        .then(function () {
          if (enrolSubmit) enrolSubmit.disabled = false;
        });
    });
  }

  // ─── Password setup from an invite link (launch gate 2026-10-26; admin/invites.ts) ───────────────────────────────────────────────────────────────────────────────
  // A founder's link is <this page>#invite=<token>. The token is read from the fragment ONCE and the fragment is removed from the address bar straight away, so it is not left in
  // the history entry, a bookmark or a screenshot. It is held in memory only (never storage). The server's answer to every kind of bad link is the same sentence, and so is this page's.
  var INVITE_RE = /^#invite=([A-Za-z0-9_-]{43})$/;
  var invitePanel = $('[data-login-invite]');
  var inviteForm = $('[data-login-invite-form]');
  var inviteError = $('[data-login-invite-error]');
  var inviteSubmit = $('[data-login-invite-submit]');
  var inviteBack = $('[data-login-invite-back]');
  var inviteSignIn = $('[data-login-invite-signin]');
  var inviteToken = null;

  function inviteFail(msg) {
    if (!inviteError) return;
    inviteError.hidden = false;
    inviteError.textContent = msg;
  }
  function leaveInvite(msg) {
    inviteToken = null;
    if (invitePanel) invitePanel.hidden = true;
    form.hidden = false;
    setMode('totp');
    if (msg) showError(msg);
  }
  (function readInviteFromFragment() {
    var m = INVITE_RE.exec(window.location.hash || '');
    if (!m || !invitePanel || !inviteForm) return;
    inviteToken = m[1];
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (_) {}
    form.hidden = true;
    invitePanel.hidden = false;
  })();
  if (inviteSignIn) inviteSignIn.addEventListener('click', function () { leaveInvite(); });

  if (inviteForm) {
    inviteForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!inviteToken) return;
      if (inviteError) {
        inviteError.hidden = true;
        inviteError.textContent = '';
      }
      var fd = new FormData(inviteForm);
      var pw = String(fd.get('new_password') || '');
      if (pw !== String(fd.get('new_password_again') || '')) {
        inviteFail('The two passwords do not match.');
        return;
      }
      if (pw.length < 12) {
        inviteFail('Use at least 12 characters.');
        return;
      }
      if (inviteSubmit) inviteSubmit.disabled = true;
      postJson(INVITE_ACCEPT_ENDPOINT, { token: inviteToken, password: pw })
        .then(function (res) {
          if (res.ok && res.data && res.data.next === 'enrol' && res.data.enrol_token) {
            inviteToken = null;
            if (invitePanel) invitePanel.hidden = true;
            startEnrolment(res.data.enrol_token);
            return;
          }
          if (res.ok && res.data && res.data.next === 'login') {
            leaveInvite('Password saved. Sign in with your new password and your authenticator code.');
            return;
          }
          if (res.status === 422) {
            inviteFail((res.data && res.data.error) || 'Choose a stronger password.');
            return;
          }
          if (res.status === 401) {
            inviteToken = null;
            if (inviteForm) inviteForm.hidden = true;
            if (inviteBack) inviteBack.hidden = false;
            inviteFail((res.data && res.data.error) || 'This link is no longer valid. Ask a founder to send you a new one.');
            return;
          }
          inviteFail((res.data && res.data.error) || 'Could not save your password. Try again.');
        })
        .catch(function () {
          inviteFail('Network error. Try again.');
        })
        .then(function () {
          if (inviteSubmit) inviteSubmit.disabled = false;
        });
    });
  }

  // The ten codes are shown ONCE and the person is held here until they say they have saved them. They live only in this page's DOM: no storage, no URL, no log.
  function showBackupCodes(codes) {
    if (enrolPanel) enrolPanel.hidden = true;
    if (codesPanel) codesPanel.hidden = false;
    if (codesList) codesList.textContent = codes.join('\n');
    var ack = $('[data-login-codes-ack]');
    var cont = $('[data-login-codes-continue]');
    var copyBtn = $('[data-login-codes-copy]');
    var dlBtn = $('[data-login-codes-download]');
    if (ack && cont) {
      ack.addEventListener('change', function () {
        cont.disabled = !ack.checked;
      });
    }
    if (cont) {
      cont.addEventListener('click', function () {
        if (ack && !ack.checked) return;
        if (codesList) codesList.textContent = '';
        window.location.assign(afterLoginUrl());
      });
    }
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(codes.join('\n')).then(
            function () { copyBtn.textContent = 'Copied'; },
            function () { copyBtn.textContent = 'Select and copy by hand'; }
          );
        } else {
          copyBtn.textContent = 'Select and copy by hand';
        }
      });
    }
    if (dlBtn) {
      dlBtn.addEventListener('click', function () {
        var blob = new Blob(['Circadia admin backup codes (each works once)\n\n' + codes.join('\n') + '\n'], { type: 'text/plain' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'circadia-admin-backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
      });
    }
  }
})();
